import { ListScreen, StackScreenProps } from '@rahtab/stack-navigator';
import { withSeparator, withToman } from '@rahtab/system/lib/tools/string';
import {
  DatePicker,
  getRadius,
  getSpacing,
  ItemCard,
  ListEmpty,
  Rating,
  Tag,
  Text,
  TextView,
  Picker,
} from '@rahtab/ui';
import MerchandiseListEmptyIcon from '@rahtab/ui/dist/icons/ListEmptyIcons/StoreMerchandises';
import CalenderIcon from '@rahtab/ui/dist/icons/CalenderIcon';
import LocationIcon from '@rahtab/ui/dist/icons/LocationIcon';
import useListings from 'hooks/useListings';
import useOrderingMerchandise from 'hooks/useOrderingMerchandise';
import moment from 'moment-jalaali';
import { useAppDispatch } from 'rahtab-redux';
import React, { useEffect, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import type { RootStackType } from '../../Router';
import { setListingsLoading } from 'rahtab-redux/slices/listingsSlice';
import { OrderingType } from 'hooks/useMerchandises';

const DATE_FORMAT = 'YYYY/MM/DD';
const DISPLAY_DATE_FORMAT = 'jYYYY/jMM/jDD';
const defaultProvider = require('../../../assets/images/defaultProvider.png');
const defaultMerchandise = require('../../../assets/images/defaultMerchandise.png');

const Listings: React.VFC<StoresProps> = ({ navigation: { setParams }, route: { params } }) => {
  const dispatch = useAppDispatch();
  const { merchandiseId } = params;
  const { data: orderingMerchandise } = useOrderingMerchandise(merchandiseId!);
  const [requestDate, setRequestDate] = useState(
    moment.utc(orderingMerchandise.initialDeliveryDate).format(DATE_FORMAT),
  );
  const [termCode, setTermCode] = useState('');
  const { data, loading, called, refetch, listingsLoading } = useListings({
    deliveryDate: requestDate,
    merchandiseId: merchandiseId!,
    orderingType: orderingMerchandise.orderingType,
    termCode,
  });
  useEffect(() => {
    if (listingsLoading === 'refreshing') {
      if (called) refetch();
      dispatch(setListingsLoading('idle'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingsLoading]);

  useEffect(() => {
    if (termCode || !data || orderingMerchandise.orderingType !== OrderingType.PERIODIC) return;
    setTermCode(data.termList[data.termList.length - 1]?.TermCode || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <ListScreen
      title="فروشندگان کالا"
      data={{ ...orderingMerchandise, ...data }}
      loading={loading}
      refreshing={called && loading}
      onRefresh={refetch}
      renderControls={({
        name,
        imageUrl,
        rating,
        ratingCount,
        sellerCount,
        initialDeliveryDate,
        merchandiseUnitText,
        viewerTotalOrderQuantity,
        termList,
        orderingType,
      }) => (
        <>
          <ItemCard
            title={name}
            image={imageUrl}
            defaultImage={defaultMerchandise}
            inHeader
            screenName={'OrdersHistory'}
            params={{ merchandiseId }}
            caption={
              <Text color="primary" margin="none">
                مشاهده سوابق سفارشات تامین موجودی کالا
              </Text>
            }>
            <Text color="description">
              مجموع سفارشات:{' '}
              <Text variant="cardTitle">
                {withSeparator(viewerTotalOrderQuantity)} {merchandiseUnitText}
              </Text>
            </Text>
            <Rating value={rating} count={ratingCount} />
          </ItemCard>
          <View style={styles.controls}>
            {orderingType === OrderingType.PERIODIC ? (
              <Picker
                placeholder="طرح را انتخاب نمایید."
                label="طرح"
                selectedValue={termCode}
                onSelectValue={setTermCode}
                color="primary"
                marginBottom={0}
                variant="outlined"
                customIcon={<CalenderIcon />}
                style={styles.datePicker}>
                {termList.map(({ TermCode, TermName }) => (
                  <Picker.Item key={TermCode} label={TermName} value={TermCode} />
                ))}
              </Picker>
            ) : (
              <DatePicker
                placeholder="تاریخ تحویل را انتخاب نمایید."
                label="تاریخ تحویل"
                onConfirm={setRequestDate}
                value={requestDate}
                format={DATE_FORMAT}
                displayFormat={DISPLAY_DATE_FORMAT}
                margin="none"
                style={styles.datePicker}
                sameOrAfter={initialDeliveryDate}
                variant="outlined"
              />
            )}
          </View>
        </>
      )}
      listData={data.listings}
      keyExtractor={({ id }) => id}
      ListEmptyComponent={<ListEmpty text="عرضه‌ای یافت نشد." imageComponent={<MerchandiseListEmptyIcon />} />}
      renderItem={({ item: { remainingQuantity, seller, price, id, viewerOrderLimit, active } }) => {
        const isOutOfStock = remainingQuantity === 0;
        const viewerExceedOrderLimit = viewerOrderLimit === 0;
        return (
          <ItemCard
            screenName={'SubmitOrders'}
            params={{
              listingId: id,
            }}
            hasChevron={active && !isOutOfStock}
            statusBadge={isOutOfStock ? <Tag text="اتمام موجودی" color="disabled" /> : undefined}
            title={seller.name}
            image={seller.imageUrl}
            defaultImage={defaultProvider}
            imageProps={{ resizeMode: 'cover' }}
            caption={
              <Text margin="none">
                قیمت فروش کالا:{' '}
                <Text margin="none" variant="cardTitle">
                  {withToman(price)}
                </Text>
              </Text>
            }>
            <Rating value={seller.rating} count={isNaN(seller.ratingCount) ? 0 : seller.ratingCount} />
            {seller.city ? (
              <View style={styles.textAndIcon}>
                <LocationIcon style={styles.locationIcon} />
                <Text>{seller.city}</Text>
              </View>
            ) : null}
          </ItemCard>
        );
      }}
    />
  );
};

export default Listings;

const styles = StyleSheet.create({
  controls: {
    flexDirection: 'row',
    marginTop: getSpacing(),
  },
  location: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: getSpacing(),
  },
  cardCaption: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  datePicker: {
    flex: 2,
    marginBottom: 0,
  },
  list: {
    padding: getSpacing(0.5),
  },
  marginBottom: {
    marginBottom: getSpacing(),
  },
  image: {
    height: 80,
    width: 80,
    borderRadius: getRadius(),
  },
  details: {
    marginLeft: getSpacing(),
    width: '62%',
  },
  textAndIcon: {
    flexDirection: 'row',
    bottom: -getSpacing(),
  },
  locationIcon: {
    marginRight: getSpacing(0.35),
    marginTop: Platform.OS === 'web' ? 0 : getSpacing(0.3),
  },
});

type StoresProps = StackScreenProps<RootStackType, 'Listings'>;
